/* eslint-disable react-refresh/only-export-components */
'use client'

import { Field as FieldPrimitive } from '@base-ui/react/field'
import { Fieldset as FieldsetPrimitive } from '@base-ui/react/fieldset'
import { Form as FormPrimitive } from '@base-ui/react/form'
import { cva, type VariantProps } from 'class-variance-authority'

import { Separator } from '@eigtech/ui/components/separator'
import { cn } from '@eigtech/ui/lib/utils'
import { createContext, useContext } from 'react'

function Form({ className, ...props }: React.ComponentProps<typeof FormPrimitive>) {
  return (
    <FormPrimitive className={cn('flex flex-col gap-4', className)} data-slot="form" {...props} />
  )
}

function FieldSetBase({ className, ...props }: React.ComponentProps<'fieldset'>) {
  return (
    <fieldset
      className={cn(
        'flex flex-col gap-4',
        'has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3',
        className
      )}
      data-slot="field-set"
      {...props}
    />
  )
}

function FieldSet(props: React.ComponentProps<typeof FieldsetPrimitive.Root>) {
  return <FieldsetPrimitive.Root render={<FieldSetBase />} {...props} />
}

function FieldLegendBase({
  className,
  variant = 'legend',
  ...props
}: React.ComponentProps<'legend'> & { variant?: 'legend' | 'label' }) {
  return (
    <legend
      className={cn(
        'mb-3 font-medium',
        'data-[variant=legend]:text-base',
        'data-[variant=label]:text-sm data-[variant=label]:leading-snug',
        className
      )}
      data-slot="field-legend"
      data-variant={variant}
      {...props}
    />
  )
}

function FieldLegend({
  variant = 'legend',
  ...props
}: React.ComponentProps<typeof FieldsetPrimitive.Legend> & { variant?: 'legend' | 'label' }) {
  return <FieldsetPrimitive.Legend render={<FieldLegendBase variant={variant} />} {...props} />
}

function FieldGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'group/field-group @container/field-group flex w-full flex-col gap-7 data-[slot=checkbox-group]:gap-3 *:data-[slot=field-group]:gap-4',
        className
      )}
      data-slot="field-group"
      {...props}
    />
  )
}

const fieldVariants = cva(
  'group/field data-[invalid=true]:text-destructive data-invalid:text-destructive flex w-full gap-2',
  {
    variants: {
      orientation: {
        vertical: ['flex-col *:w-full [&>.sr-only]:w-auto'],
        horizontal: [
          'flex-row items-center',
          '*:data-[slot=field-label]:flex-auto',
          'has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px has-[>[data-slot=field-content]]:items-start',
        ],
        responsive: [
          '@md/field-group:flex-row @md/field-group:items-center @md/field-group:*:w-auto flex-col *:w-full [&>.sr-only]:w-auto',
          '@md/field-group:*:data-[slot=field-label]:flex-auto',
          '@md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
        ],
      },
    },
    defaultVariants: {
      orientation: 'vertical',
    },
  }
)

const FallbackFieldContext = createContext<
  React.ComponentProps<typeof FieldPrimitive.Root> | undefined
>(undefined)

export function useFallbackFieldContext() {
  return useContext(FallbackFieldContext)
}

function FieldBase({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof fieldVariants>) {
  return (
    <div
      // data-disabled={props.disabled}
      // data-invalid={props.invalid}
      className={cn(fieldVariants({ orientation }), className)}
      data-orientation={orientation}
      data-slot="field"
      role="group"
      {...props}
    />
  )
}

function Field({
  orientation = 'vertical',
  ...props
}: React.ComponentProps<typeof FieldPrimitive.Root> & VariantProps<typeof fieldVariants>) {
  return (
    <FallbackFieldContext.Provider value={props}>
      <FieldPrimitive.Root render={<FieldBase orientation={orientation} />} {...props} />
    </FallbackFieldContext.Provider>
  )
}

function FieldItem({ className, ...props }: React.ComponentProps<typeof FieldPrimitive.Item>) {
  return <FieldPrimitive.Item className={cn('', className)} data-slot="field-item" {...props} />
}

function FieldContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('group/field-content flex flex-1 flex-col gap-1.5 leading-snug', className)}
      data-slot="field-content"
      {...props}
    />
  )
}

function FieldLabelBase({ className, ...props }: React.ComponentProps<'label'>) {
  return (
    <label
      className={cn(
        'group/field-label peer/field-label flex w-fit gap-2 text-sm font-medium leading-snug group-data-[disabled=true]/field:opacity-50',
        'has-[>[data-slot=field]]:border-border has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col has-[>[data-slot=field]]:rounded-md has-[>[data-slot=field]]:border *:data-[slot=field]:p-4',
        'has-data-checked:bg-background/70 has-data-[state=checked]:bg-background',
        className
      )}
      data-slot="field-label"
      {...props}
    />
  )
}

function FieldLabel(props: React.ComponentProps<typeof FieldPrimitive.Label>) {
  return <FieldPrimitive.Label render={<FieldLabelBase />} {...props} />
}

function FieldTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex w-fit items-center gap-2 text-sm font-medium leading-snug group-data-[disabled=true]/field:opacity-50',
        className
      )}
      data-slot="field-label"
      {...props}
    />
  )
}

function FieldDescription({
  className,
  ...props
}: React.ComponentProps<typeof FieldPrimitive.Description>) {
  return (
    <FieldPrimitive.Description
      className={cn(
        'text-muted-foreground group-has-data-[orientation=horizontal]/field:text-balance text-sm font-normal leading-normal',
        'nth-last-2:-mt-1 last:mt-0 [[data-variant=legend]+&]:-mt-1.5',
        '[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4',
        className
      )}
      data-slot="field-description"
      {...props}
    />
  )
}

function FieldSeparator({ children, className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2',
        className
      )}
      data-content={!!children}
      data-slot="field-separator"
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          className="bg-background text-muted-foreground relative mx-auto block w-fit px-2"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  )
}

function FieldError({ className, ...props }: React.ComponentProps<typeof FieldPrimitive.Error>) {
  return (
    <FieldPrimitive.Error
      className={cn(
        'text-destructive text-sm font-normal [&_ul]:ml-4 [&_ul]:flex [&_ul]:list-disc [&_ul]:flex-col [&_ul]:gap-1',
        className
      )}
      data-slot="field-error"
      role="alert"
      {...props}
    />
  )
}

export {
  Field,
  FieldBase,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldItem,
  FieldLabel,
  FieldLabelBase,
  FieldLegend,
  FieldLegendBase,
  FieldSeparator,
  FieldSet,
  FieldSetBase,
  FieldTitle,
  Form,
}
